import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { 
  ArrowLeft, 
  ArrowRight, 
  Save, 
  Upload, 
  FileText, 
  Loader2, 
  Mic, 
  Video, 
  Presentation,
  Check,
  Bell
} from "lucide-react";
import { AnalysisLoader } from "./AnalysisLoader";
import { showSuccess, showError } from "@/utils/toast";
import { apiClient } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import logo from '../assets/images/logo-artie.png';

// Schema matching new backend requirements
const projectSchema = z.object({
  title: z.string().min(1, "Project name is required"),
  logline: z.string().min(10, "Logline must be at least 10 characters"),
  // brand_revenue_target matches "How much do you need from brand partners?"
  brand_revenue_target: z.number().min(0, "Amount must be 0 or greater"),
  flexibility_preference: z.enum(["very_flexible", "flexible_with_limits", "selective"], {
    required_error: "Please select an option",
  }),
  flexibility_details: z.string().optional(),
});

type ProjectFormData = z.infer<typeof projectSchema>;

interface ProjectIntakeFormProps {
  onProjectCreated: (projectData: any) => void;
  onBack: () => void;
}

const STEPS = [
  { id: "title", label: "Project Title" },
  { id: "script", label: "Script Upload" },
  { id: "logline", label: "Logline" },
  { id: "budget", label: "Budget Target" },
  { id: "flexibility", label: "Flexibility" },
] as const;

const NotifyButton = ({ label = "Notify" }: { label?: string }) => {
  const [isRinging, setIsRinging] = useState(false);
  const [notified, setNotified] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!notified) {
      setIsRinging(true);
      setNotified(true);
      setTimeout(() => setIsRinging(false), 500);
    } else {
      setNotified(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      type="button"
      onClick={handleClick}
      className={cn(
        "h-6 px-2 text-[10px] transition-all",
        "bg-slate-100 hover:bg-slate-200",
        "text-slate-500 hover:text-slate-900",
        notified && "bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800"
      )}
    >
      <Bell
        className={cn(
          "h-3 w-3 mr-1",
          isRinging && "animate-bell-ring"
        )}
        fill={notified ? "currentColor" : "none"}
      />
      {notified ? "Notified" : label}
    </Button>
  );
};

export const ProjectIntakeForm = ({ onProjectCreated, onBack }: ProjectIntakeFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [extractingLogline, setExtractingLogline] = useState(false);
  const [uploadedScriptId, setUploadedScriptId] = useState<string | null>(null);
  
  const form = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      title: "",
      logline: "",
      brand_revenue_target: undefined,
      flexibility_preference: "very_flexible", // Default
      flexibility_details: "",
    },
    mode: "onChange",
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      console.log("File selected:", {
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      // Validate file type
      const validTypes = ['.pdf', '.docx', '.txt'];
      const isTypeValid = validTypes.some(type => file.name.toLowerCase().endsWith(type));
      
      if (!isTypeValid) {
        showError("Please upload a PDF, DOCX, or TXT file");
        return;
      }

      // Max 180 pages approximation (assuming 2KB per page for text or checking PDF is harder purely frontend without lib)
      // For now, let's just limit by size as a proxy, e.g., 10MB
      if (file.size > 10 * 1024 * 1024) {
        showError("File size too large. Please upload a smaller file.");
        return;
      }

      setSelectedFile(file);
      console.log("File set to state:", file.name, file.size);
      
      // Upload script immediately to get script ID for logline extraction
      await uploadScriptForLogline(file);
    }
  };

  const uploadScriptForLogline = async (file: File) => {
    try {
      setExtractingLogline(true);
      
      // Create a temporary project to upload the script
      const tempPayload = {
        title: form.getValues("title") || "Temp Project",
        logline: "",
        status: "draft",
        brand_revenue_target: 0,
        flexibility_preference: "very_flexible" as const,
        genre: [],
        format: "Feature Length Film",
        help_needed: [],
        budget: 0,
        target_30d_reach: 0,
        launch_date: new Date().toISOString().split('T')[0],
      };
      
      const tempProject = await apiClient.createProject(tempPayload);
      // @ts-ignore
      const projectId = tempProject.id || tempProject._id;
      
      if (!projectId) throw new Error("Failed to create temporary project");

      // Upload script
      const scriptResult = await apiClient.uploadScript({
        projectId,
        file,
        title: file.name,
      });
      
      // @ts-ignore
      const scriptId = scriptResult.script_id || scriptResult.id || scriptResult._id;
      setUploadedScriptId(scriptId);
      
      console.log("✅ Script uploaded with ID:", scriptId);
      
      // Wait a bit for processing to start
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      console.log("⏳ Script processing complete, ready for extraction");
      
      // Upload complete, enable the Next button
      setExtractingLogline(false);
      
    } catch (error) {
      console.error("❌ Error uploading script:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to upload script for analysis";
      showError(errorMessage);
      setExtractingLogline(false);
    }
  };

  const extractLoglineFromScript = async () => {
    console.log("🎬 Starting logline extraction, script ID:", uploadedScriptId);
    
    if (!uploadedScriptId) {
      console.warn("⚠️ No script ID available");
      const placeholderLogline = "A compelling story that will captivate audiences.";
      form.setValue("logline", placeholderLogline);
      showError("Please write your own logline.");
      return;
    }
    
    setExtractingLogline(true);
    try {
      // Wait for script processing to complete (poll for status)
      let attempts = 0;
      const maxAttempts = 15; // Increased attempts
      
      console.log(`🔄 Attempting to extract logline (max ${maxAttempts} attempts)...`);
      
      while (attempts < maxAttempts) {
        try {
          console.log(`📝 Attempt ${attempts + 1}/${maxAttempts} - Calling extract API...`);
          const result = await apiClient.extractLogline(uploadedScriptId);
          console.log("✅ Logline extracted successfully:", result.logline);
          form.setValue("logline", result.logline);
          showSuccess("Logline extracted! Feel free to edit it.");
          return;
        } catch (error: any) {
          console.log(`⚠️ Attempt ${attempts + 1} failed:`, error.message);
          
          if (error.message?.includes("not available yet") || error.message?.includes("processing")) {
            // Script still processing, wait and retry
            console.log("⏳ Script still processing, waiting 3 seconds...");
            await new Promise(resolve => setTimeout(resolve, 3000));
            attempts++;
          } else {
            console.error("❌ Extraction error:", error);
            throw error;
          }
        }
      }
      
      // If we get here, extraction timed out
      console.error("⏱️ Script processing timed out after", maxAttempts, "attempts");
      throw new Error("Script processing timed out");
      
    } catch (error) {
      console.error("❌ Final error extracting logline:", error);
      // Set a placeholder if extraction fails
      const placeholderLogline = "A compelling story that will captivate audiences.";
      form.setValue("logline", placeholderLogline);
      showError("Could not extract logline automatically. Please write your own.");
    } finally {
      setExtractingLogline(false);
    }
  };

  const handleCreateProject = async (data: ProjectFormData) => {
    if (!selectedFile) {
      showError("Please upload a script");
      return;
    }

    try {
      setIsSubmitting(true);
      
      // 1. Create Project
      const payload = {
        title: data.title,
        logline: data.logline,
        status: "pending",
        brand_revenue_target: data.brand_revenue_target,
        flexibility_preference: data.flexibility_preference,
        flexibility_details: data.flexibility_details,
        // Defaults for required fields in DB but not in wizard
        genre: [],
        format: "Feature Length Film", // Default or "TBD"
        help_needed: [],
        budget: 0,
        target_30d_reach: 0,
        launch_date: new Date().toISOString().split('T')[0],
      };
      
      const createdProject = await apiClient.createProject(payload);
      // @ts-ignore
      const projectId = createdProject.id || createdProject._id;
      
      if (!projectId) throw new Error("Project created but ID missing");

      // 2. Upload Script
      const scriptResult = await apiClient.uploadScript({
        projectId,
        file: selectedFile,
        title: selectedFile.name,
      });
      
      // Store script ID for potential logline extraction
      // @ts-ignore
      const scriptId = scriptResult.script_id || scriptResult.id || scriptResult._id;
      setUploadedScriptId(scriptId);

      // 3. Complete
      onProjectCreated(createdProject);
      
    } catch (error) {
      console.error("Error creating project:", error);
      showError(error instanceof Error ? error.message : "Failed to create project");
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = async () => {
    console.log("📍 nextStep called - currentStep:", currentStep, "selectedFile:", !!selectedFile, "uploadedScriptId:", uploadedScriptId);
    
    // Custom validation for script step
    if (currentStep === 1 && !selectedFile) {
      showError("Please upload a script to continue");
      return;
    }

    // Form validation for current step
    let isValid = false;
    const stepId = STEPS[currentStep].id;
    
    if (stepId === "title") {
      isValid = await form.trigger("title");
    } else if (stepId === "script") {
      isValid = true; // Script validation already done above
    } else if (stepId === "logline") {
      isValid = await form.trigger("logline");
    } else if (stepId === "budget") {
      isValid = await form.trigger("brand_revenue_target");
    } else if (stepId === "flexibility") {
      isValid = await form.trigger(["flexibility_preference", "flexibility_details"]);
    } else {
      isValid = true;
    }

    if (!isValid) {
      console.log("❌ Validation failed for step:", stepId);
      return;
    }

    // Check if we should extract logline BEFORE moving to next step
    if (currentStep === 1 && selectedFile && uploadedScriptId) {
      console.log("🎬 About to move to logline step, starting extraction...");
      // Move to next step first so user sees the loading state
      setCurrentStep(prev => prev + 1);
      // Then start extraction
      await extractLoglineFromScript();
    } else if (currentStep < STEPS.length - 1) {
      // Just move to next step
      console.log("➡️ Moving to next step without extraction");
      setCurrentStep(prev => prev + 1);
    } else {
      // Final submission
      console.log("✅ Final step - submitting project");
      handleCreateProject(form.getValues());
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    } else {
      onBack();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
      e.preventDefault();
      nextStep();
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Title
        return (
          <div key="step-title" className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
            {/* Artie Guide */}
            <div className="flex items-start gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm mb-6">
              <div className="hidden sm:block shrink-0">
                 <div className="w-14 h-14 rounded-full bg-slate-100 border-2 border-white shadow-md overflow-hidden">
                    <img src={logo} alt="Artie" className="w-full h-full object-cover" />
                 </div>
              </div>
              <div className="space-y-1">
                <h2 className="text-xl font-bold text-slate-900">What is your project called?</h2>
                <p className="text-slate-500 text-sm leading-relaxed">
                   Let's start with a name. Give your project a memorable title—something that captures the vibe.
                </p>
              </div>
            </div>
            
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <input
                      autoFocus
                      placeholder="e.g. The Next Big Thing"
                      {...field}
                      className="w-full border-0 border-b-2 border-slate-200 focus:border-slate-900 rounded-none px-0 py-2 text-2xl md:text-3xl bg-transparent transition-colors focus:outline-none focus:ring-0 placeholder:text-slate-300 font-medium"
                      onKeyDown={handleKeyDown}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        );

      case 1: // Script Upload
        return (
          <div key="step-script" className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
             {/* Artie Guide */}
            <div className="flex items-start gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm mb-6">
              <div className="hidden sm:block shrink-0">
                 <div className="w-14 h-14 rounded-full bg-slate-100 border-2 border-white shadow-md overflow-hidden">
                    <img src={logo} alt="Artie" className="w-full h-full object-cover" />
                 </div>
              </div>
              <div className="space-y-1">
                <h2 className="text-xl font-bold text-slate-900">Upload your script</h2>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Upload what you have right now. Artie looks for patterns and potential, not polish. Your work stays private.
                </p>
              </div>
            </div>

            {/* Main Upload Area */}
            <div 
              className={cn(
                "border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center text-center transition-all cursor-pointer bg-white",
                selectedFile ? "border-green-500 bg-green-50/30" : "border-slate-300 hover:border-slate-400 hover:bg-slate-50"
              )}
              onClick={() => fileInputRef.current?.click()}
            >
              <Input
                type="file"
                accept=".pdf,.docx,.txt"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileSelect}
              />
              
              {selectedFile ? (
                <>
                  <div className="h-16 w-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                    <Check className="h-8 w-8" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 break-all max-w-full">
                    {selectedFile.name}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {selectedFile.size < 1024 * 1024
                      ? `${(selectedFile.size / 1024).toFixed(2)} KB`
                      : `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`
                    }
                  </p>
                  <Button variant="ghost" className="mt-4 text-slate-500 hover:text-red-500" onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    // Reset the file input so the same file can be selected again
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}>
                    Remove and upload different file
                  </Button>
                </>
              ) : (
                <>
                  <div className="h-16 w-16 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center mb-4">
                    <Upload className="h-8 w-8" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    Click to upload or drag and drop
                  </h3>
                  <p className="text-sm text-slate-500 mt-2 max-w-xs">
                    PDF, DOCX, or TXT (Max 180 pages)
                  </p>
                </>
              )}
            </div>

            {/* Coming Soon Options */}
            <div className="space-y-3">
               <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Coming Soon</p>
               <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                 {[
                   { icon: Presentation, label: "Pitch Decks" },
                   { icon: Video, label: "Video Files" },
                   { icon: Mic, label: "Audio Files" },
                 ].map((item, i) => (
                 <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="flex items-center space-x-3">
                      <item.icon className="h-4 w-4 text-slate-600" />
                      <span className="text-sm font-medium text-slate-700">{item.label}</span>
                    </div>
                    <NotifyButton key={`script-notify-${i}`} />
                 </div>
               ))}
               </div>
            </div>
          </div>
        );

      case 2: // Logline
        return (
          <div key="step-logline" className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
             {/* Artie Guide */}
            <div className="flex items-start gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm mb-6">
              <div className="hidden sm:block shrink-0">
                 <div className="w-14 h-14 rounded-full bg-slate-100 border-2 border-white shadow-md overflow-hidden">
                    <img src={logo} alt="Artie" className="w-full h-full object-cover" />
                 </div>
              </div>
              <div className="space-y-1">
                <h2 className="text-xl font-bold text-slate-900">What's your story about?</h2>
                <p className="text-slate-500 text-sm leading-relaxed">
                  I've extracted a logline from your script. Does this capture the essence of your story? Feel free to tweak it.
                </p>
              </div>
            </div>

            <FormField
              control={form.control}
              name="logline"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      autoFocus
                      placeholder="A gripping tale of..."
                      {...field}
                      disabled={extractingLogline}
                      className="w-full border-2 border-slate-200 focus:border-slate-900 rounded-lg px-4 py-3 text-base bg-white transition-colors focus:outline-none focus:ring-0 placeholder:text-slate-300 min-h-[120px] resize-none"
                    />
                  </FormControl>
                  <FormMessage />
                  {extractingLogline && (
                    <div className="flex items-center gap-2 text-sm text-slate-500 mt-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Analyzing your script...
                    </div>
                  )}
                </FormItem>
              )}
            />

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>Tip:</strong> A good logline is 1-2 sentences that captures the essence of your story, including the protagonist, their goal, and the main conflict.
              </p>
            </div>
          </div>
        );

      case 3: // Budget Target
        return (
          <div key="step-budget" className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
             {/* Artie Guide */}
            <div className="flex items-start gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm mb-6">
              <div className="hidden sm:block shrink-0">
                 <div className="w-14 h-14 rounded-full bg-slate-100 border-2 border-white shadow-md overflow-hidden">
                    <img src={logo} alt="Artie" className="w-full h-full object-cover" />
                 </div>
              </div>
              <div className="space-y-1">
                <h2 className="text-xl font-bold text-slate-900">How much do you need?</h2>
                <p className="text-slate-500 text-sm leading-relaxed">
                   Set your funding goal. This helps me find brand partners who can match your budget expectations.
                </p>
              </div>
            </div>

            <FormField
              control={form.control}
              name="brand_revenue_target"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 text-3xl md:text-4xl text-slate-300 font-light">$</span>
                      <input
                        autoFocus
                        type="number"
                        min="0"
                        placeholder="0"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                        className="w-full border-0 border-b-2 border-slate-200 focus:border-slate-900 rounded-none pl-8 md:pl-10 pr-0 py-2 text-3xl md:text-5xl bg-transparent transition-colors focus:outline-none focus:ring-0 placeholder:text-slate-200 font-bold text-slate-900"
                        onKeyDown={handleKeyDown}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Coming Soon Goals */}
            <div className="pt-8 space-y-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Other Goals (Coming Soon)</p>
              <div className="flex flex-col gap-2">
                 {[
                   "Find equity investors",
                   "Find producers / collaborators"
                 ].map((goal, i) => (
                   <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white shadow-sm">
                      <span className="text-sm font-medium text-slate-700">{goal}</span>
                      <NotifyButton key={`budget-notify-${i}`} label="Notify me" />
                   </div>
                 ))}
              </div>
            </div>
          </div>
        );

      case 4: // Flexibility
        return (
          <div key="step-flexibility" className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
             {/* Artie Guide */}
            <div className="flex items-start gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm mb-6">
              <div className="hidden sm:block shrink-0">
                 <div className="w-14 h-14 rounded-full bg-slate-100 border-2 border-white shadow-md overflow-hidden">
                    <img src={logo} alt="Artie" className="w-full h-full object-cover" />
                 </div>
              </div>
              <div className="space-y-1">
                <h2 className="text-xl font-bold text-slate-900">How flexible are you?</h2>
                <p className="text-slate-500 text-sm leading-relaxed">
                   Brands love flexibility, but your creative vision comes first. Tell me how you want to work with partners.
                </p>
              </div>
            </div>

            <FormField
              control={form.control}
              name="flexibility_preference"
              render={({ field }) => (
                <FormItem className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    {[
                      { 
                        value: "very_flexible", 
                        label: "Very flexible", 
                        desc: "No strict rules, optimize for my revenue goal." 
                      },
                      { 
                        value: "flexible_with_limits", 
                        label: "Flexible with limits", 
                        desc: "Open to ideas, but I have some boundaries." 
                      },
                      { 
                        value: "selective", 
                        label: "Selective", 
                        desc: "I have specific types of integrations in mind." 
                      }
                    ].map((option) => (
                      <div
                        key={option.value}
                        onClick={() => field.onChange(option.value)}
                        className={cn(
                          "cursor-pointer rounded-xl border-2 p-5 transition-all hover:border-slate-300",
                          field.value === option.value
                            ? "border-slate-900 bg-slate-50 ring-0"
                            : "border-slate-100 bg-white"
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-slate-900 text-lg">{option.label}</h3>
                            <p className="text-slate-500">{option.desc}</p>
                          </div>
                          <div className={cn(
                            "h-5 w-5 rounded-full border-2 flex items-center justify-center",
                            field.value === option.value ? "border-slate-900" : "border-slate-300"
                          )}>
                             {field.value === option.value && <div className="h-2.5 w-2.5 rounded-full bg-slate-900" />}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Conditional Follow-up Questions */}
            {form.watch("flexibility_preference") === "flexible_with_limits" && (
               <FormField
               control={form.control}
               name="flexibility_details"
               render={({ field }) => (
                 <FormItem className="animate-in fade-in slide-in-from-top-2">
                   <FormLabel className="text-slate-900 font-medium">What are your rules or boundaries?</FormLabel>
                   <FormControl>
                     <Textarea 
                       {...field} 
                       placeholder="e.g. No alcohol or tobacco brands, keep logo placement subtle..."
                       className="resize-none min-h-[100px] text-base"
                     />
                   </FormControl>
                   <FormMessage />
                 </FormItem>
               )}
             />
            )}

            {form.watch("flexibility_preference") === "selective" && (
               <FormField
               control={form.control}
               name="flexibility_details"
               render={({ field }) => (
                 <FormItem className="animate-in fade-in slide-in-from-top-2">
                   <FormLabel className="text-slate-900 font-medium">What types of integrations are you open to?</FormLabel>
                   <FormControl>
                     <Textarea 
                       {...field} 
                       placeholder="e.g. Only interested in product placement, no spoken ads..."
                       className="resize-none min-h-[100px] text-base"
                     />
                   </FormControl>
                   <FormMessage />
                 </FormItem>
               )}
             />
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (isSubmitting) {
    return <AnalysisLoader />;
  }

  return (
    <div className="min-h-screen bg-cream-yellow flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-xl">
        {/* Progress */}
        <div className="mb-10 flex items-center justify-between">
            <div className="flex space-x-2">
              {STEPS.map((_, idx) => (
                <div 
                  key={idx} 
                  className={cn(
                    "h-2 rounded-full transition-all duration-300",
                    idx <= currentStep ? "w-8 bg-slate-900" : "w-2 bg-slate-200"
                  )}
                />
              ))}
            </div>
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Step {currentStep + 1} of {STEPS.length}
            </span>
        </div>

        <Form {...form}>
          <form onSubmit={(e) => e.preventDefault()} className="space-y-8">
            <div className="min-h-[400px]">
              {renderStepContent()}
            </div>

            <div className="flex items-center justify-between pt-6 border-t border-slate-200/50">
               <Button
                type="button"
                variant="ghost"
                onClick={prevStep}
                disabled={isSubmitting}
                className="text-slate-500 hover:text-slate-900 -ml-4"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>

              <Button
                type="button"
                onClick={nextStep}
                disabled={isSubmitting || (currentStep === 1 && extractingLogline)}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 h-12 text-base shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {currentStep === STEPS.length - 1 ? (
                  <>
                    {isSubmitting ? (
                      <>
                        Creating... <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                      </>
                    ) : (
                      <>
                        Get Artie Score
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </>
                ) : currentStep === 1 && extractingLogline ? (
                  <>
                    Uploading... <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                  </>
                ) : (
                  <>
                    Next Step
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};